import Dexie, { type EntityTable } from 'dexie';
import type { ActiveTimer, AgentRun, Change, Label, Project, Session, Settings, Task } from '@timato/shared';

export type OutboxEntry = Change & { seq?: number };
export type MetaEntry = { key: string; value: unknown };

/**
 * Local source of truth for the personal plane. All ids are UUIDv7 from the client; every row carries
 * an HLC `updatedAt` and a soft `deletedAt` so sync is a pure function of these tables (§7).
 */
export class TimatoDB extends Dexie {
  settings!: EntityTable<Settings, 'id'>;
  activeTimer!: EntityTable<ActiveTimer, 'id'>;
  labels!: EntityTable<Label, 'id'>;
  projects!: EntityTable<Project, 'id'>;
  tasks!: EntityTable<Task, 'id'>;
  sessions!: EntityTable<Session, 'id'>;
  agentRuns!: EntityTable<AgentRun, 'id'>;
  outbox!: EntityTable<OutboxEntry, 'seq'>;
  meta!: EntityTable<MetaEntry, 'key'>;

  constructor(name = 'timato') {
    super(name);
    this.version(2).stores({
      settings: 'id',
      activeTimer: 'id',
      labels: 'id, teamId, name, retiredAt, deletedAt',
      projects: 'id, teamId, archivedAt, position, deletedAt',
      tasks: 'id, projectId, finishedAt, position, deletedAt, *labelIds',
      sessions: 'id, startedAt, projectId, taskId, mode, status, deletedAt, *labelIds',
      outbox: '++seq, table, rowId',
      meta: 'key',
    });
    // v3: labels ordered by position (Settings › Labels, pickers).
    this.version(3).stores({ labels: 'id, teamId, name, position, retiredAt, deletedAt' });
    // v4: agent runs (§11.3). `endedAt` is indexed because "in flight" is the query the timer screen makes.
    this.version(4).stores({ agentRuns: 'id, taskId, projectId, startedAt, endedAt, deletedAt' });
  }
}

export const db = new TimatoDB();
